import { type QueryClient, useQueryClient } from "@tanstack/vue-query";
import { type MaybeRefOrGetter, ref, toValue, watch } from "vue";
import { checkForNewDeploy } from "@/app/deploy-watch";
import { getAuthToken } from "@/shared/http/client";

/** Onde a escolha do operador mora. Uma preferência DESTE navegador, nunca do workspace. */
const SOUND_KEY = "benvox.inbox.queue-sound";

/**
 * O aviso de "uma conversa entrou na fila".
 *
 * ── POR QUE NÃO PELO STREAM DA CONVERSA ───────────────────────────────────
 *
 * `GET /v3/conversations/:id/stream` alcança quem ABRIU aquela conversa. A que acabou de
 * entrar na fila é, por definição, a que ninguém abriu — então o aviso que existe para
 * dizer "alguém precisa pegar isto" é justamente o que aquele canal não entrega. Este
 * assina por TENANT (`queue/stream`), e o servidor só manda por ali os eventos que
 * significam algo fora de uma thread.
 *
 * ── DESLIGADO POR PADRÃO, E VISÍVEL ───────────────────────────────────────
 *
 * Um som que começa a tocar sozinho num painel aberto o dia inteiro é a coisa que faz
 * alguém fechar a aba. O interruptor fica no cabeçalho da lista e no menu do usuário, e o
 * padrão é `false`: ligar é uma decisão de quem ouve — e o STREAM só abre quando está
 * ligado, nada de segurar uma conexão para um som que ninguém pediu.
 *
 * ── POR QUE O ESTADO É DO MÓDULO E NÃO DO COMPONENTE ──────────────────────
 *
 * Porque agora há dois interruptores (a lista e o menu) e um só ouvinte. Um `ref` por
 * chamada faria os dois botões discordarem, e um `AbortController` por chamada abriria
 * uma segunda conexão — dois bipes para a mesma conversa — e mataria a do shell ao sair
 * de /inbox. Uma instância por ABA: o estado aqui, a conexão em `useQueueStream`, que só
 * o shell monta porque só ele nunca desmonta.
 *
 * ── POR QUE `fetch` E NÃO `EventSource` ───────────────────────────────────
 *
 * O mesmo motivo de `use-conversation-stream.ts`: `EventSource` não carrega o
 * `Authorization`, e esta rota é autenticada como todas as outras.
 */
const enabled = ref(readStoredFlag());
let controller: AbortController | null = null;
/** Uma queda é normal (deploy, wi-fi, proxy); reabrir em laço quente, não. */
const RETRY_MS = 5000;

/** O interruptor, para qualquer tela que queira desenhá-lo. Não conecta nada. */
export function useQueueAlert() {
	return { enabled, toggle };
}

function toggle(): void {
	enabled.value = !enabled.value;
	// Ligar toca a prévia NA HORA, como na V2: quem escolheu ouve o que escolheu —
	// e o clique é o gesto de usuário que destrava o áudio do navegador, para que
	// o primeiro aviso de verdade não nasça mudo.
	if (enabled.value) beep();
	try {
		localStorage.setItem(SOUND_KEY, enabled.value ? "1" : "0");
	} catch {
		// Navegação privada, cota estourada: a preferência vale por esta sessão.
	}
}

/**
 * A conexão, montada UMA VEZ pelo shell — e SEMPRE ligada com sessão.
 *
 * Era o canal do bipe e só abria com o som ligado. Virou a ESPINHA do inbox
 * (27/08, o dono baniu rota em polling permanente): o servidor agora manda por
 * aqui `inbound` e `done` além de `queued`, e é este stream que invalida as
 * consultas da caixa quando algo ACONTECE — a lista, a thread e os contadores
 * desligaram os relógios e dependem disto. O som continua sendo uma escolha
 * (`enabled`); a conexão não é mais.
 *
 * `signedIn` não é zelo: o shell também roda nas rotas públicas e a preferência
 * sobrevive ao logout, então sem ele um formulário público abriria um stream com
 * `Bearer ` vazio e o reconectaria a cada cinco segundos, para sempre.
 */
export function useQueueStream(signedIn: MaybeRefOrGetter<boolean>): void {
	const client = useQueryClient();
	watch(
		() => toValue(signedIn),
		(session) => {
			controller?.abort();
			controller = null;
			if (!session) return;
			controller = new AbortController();
			void open(client, controller.signal);
		},
		{ immediate: true },
	);
}

/**
 * Dois tons curtos, sintetizados na hora.
 *
 * Sem arquivo e sem dependência: um `.mp3` seria um recurso a servir, a versionar e
 * a esperar carregar antes do primeiro aviso. O `AudioContext` só é criado no
 * evento, porque um contexto criado antes de qualquer gesto do usuário nasce
 * suspenso em todo navegador — e ficaria mudo justamente na primeira vez.
 */
function beep(): void {
	try {
		const audio = new AudioContext();
		const gain = audio.createGain();
		gain.gain.setValueAtTime(0.06, audio.currentTime);
		gain.connect(audio.destination);
		for (const [index, hz] of [880, 1174].entries()) {
			const tone = audio.createOscillator();
			tone.frequency.value = hz;
			tone.connect(gain);
			tone.start(audio.currentTime + index * 0.16);
			tone.stop(audio.currentTime + index * 0.16 + 0.12);
		}
		setTimeout(() => void audio.close(), 800);
	} catch {
		// Sem áudio disponível o aviso vira só o refetch abaixo, que é a metade que
		// não pode faltar: a lista mostra a conversa nova de qualquer jeito.
	}
}

/**
 * Uma rajada de eventos, uma invalidação por CHAVE. A Meta entrega
 * `sent`+`delivered`+`read` juntos e um turno fecha com `done` colado no
 * `inbound` seguinte; sem a janela curta cada quadro viraria um refetch
 * próprio. As chaves pendentes acumulam num conjunto para a mesma janela
 * servir também aos pulsos — um turno que debita créditos chega como
 * `done`+`pulse(credit)` no mesmo instante, e vira UMA rodada de refetch.
 */
const KEYS_OF: Record<string, readonly (readonly string[])[]> = {
	// `help` junto do inbox: a pergunta que a Clara levanta chega no `done` do
	// turno, e o painel dela na conversa também vive sem relógio.
	inbox: [["inbox"], ["help"]],
	missions: [["missions"]],
	// `help` junto: pergunta e lacuna são a MESMA linha (human_request) — abrir,
	// responder e dispensar mexem nas duas telas e nos dois crachás.
	gaps: [["memory", "gaps"], ["help"]],
	notifications: [["notifications"]],
	credit: [["credits"]],
	// A ficha corrigida chega às telas: contato não é registro de `structure`
	// (nenhum `records:*` fala por ele), e nome/avatar moram TAMBÉM na lista da
	// caixa e no cabeçalho do chat — por isso `inbox` relê junto (07/09).
	contacts: [["contacts"], ["inbox"]],
	// A CONVERSA MEXEU — mensagem entrou, saiu, ficou segurada (08/09). A caixa
	// já ouve o fio de cada conversa; o QUADRO lia "última mensagem / sem
	// resposta" a cada 15s, e o dono via o card mover na hora e a conversa dele
	// não. Só a leitura das conversas do quadro relê — colunas e cards, não.
	conversations: [["board", "conversations"]],
	// A ESTRUTURA MEXEU (19/09): o Ben criou um campo, uma tela ou uma vista pela
	// porta única, e a tabela aberta continuava com as colunas de um minuto atrás
	// (`staleTime` de 60 s). O prefixo apanha as entidades E as telas-componente,
	// e o menu — que lê a mesma consulta — ganha a tela nova no mesmo quadro.
	// E os funis da tela de canais (29/09): "guarda pessoas" é um campo de contato
	// na entidade, e um funil criado pelo Ben não aparecia no "Conectar" sem F5.
	// E "Seu sistema" (19/10): o histórico e o Desfazer do topo, que o registro de
	// mudanças avisa a cada linha gravada, por qualquer porta.
	structure: [["structure"], ["channels", "funnels"], ["system"]],
};
const pending = new Set<string>();
/** As entidades cujas tabelas devem reler, acumuladas na MESMA janela. */
const pendingRecords = new Set<string>();
/**
 * DUAS JANELAS, e a diferença é quem está a olhar.
 *
 * `BURST_MS` é a rajada de mensagens que o comentário acima descreve: a Meta
 * entrega `sent`+`delivered`+`read` juntos, uma importação escreve quinhentas
 * linhas, e refazer a leitura a cada quadro seria uma tempestade de requisições.
 * Ninguém sente meio segundo numa lista que já está na tela.
 *
 * `PULSE_MS` é o AVISO — sino, missão, curadoria, crédito. Ele já vem coalescido
 * do servidor (um pulso por acontecimento, só o tópico viaja), e é justamente o
 * que alguém está à espera de ver aparecer: meio segundo de atraso deliberado
 * num crachá que devia acender "na hora" é o que fazia a notificação de uma
 * automação parecer que só chegava com F5. A janela curta continua a juntar a
 * rajada — cinco avisos do mesmo instante ainda são uma releitura só.
 */
const BURST_MS = 600;
const PULSE_MS = 50;
let refreshing: ReturnType<typeof setTimeout> | null = null;
/** O prazo do temporizador em curso, para um pedido mais urgente o encurtar. */
let refreshingIn = Number.POSITIVE_INFINITY;

function refreshSoon(client: QueryClient, what: keyof typeof KEYS_OF): void {
	pending.add(what);
	schedule(client, what === "inbox" ? BURST_MS : PULSE_MS);
}

function refreshRecordsSoon(client: QueryClient, entityCode: string): void {
	pendingRecords.add(entityCode);
	schedule(client, BURST_MS);
}

function schedule(client: QueryClient, delay: number): void {
	// Um pedido mais lento não estica o que já está marcado; um mais rápido
	// reagenda, porque a rajada que ele acompanha continua a caber na janela.
	if (refreshing && delay >= refreshingIn) return;
	if (refreshing) clearTimeout(refreshing);
	refreshingIn = delay;
	refreshing = setTimeout(() => {
		refreshing = null;
		refreshingIn = Number.POSITIVE_INFINITY;
		const topics = [...pending];
		pending.clear();
		for (const topic of topics) {
			for (const key of KEYS_OF[topic] ?? []) {
				void client.invalidateQueries({ queryKey: key });
			}
		}
		const entities = [...pendingRecords];
		pendingRecords.clear();
		for (const code of entities) {
			void client.invalidateQueries({ queryKey: ["records", code] });
		}
		if (entities.length) {
			void client.invalidateQueries({ queryKey: ["board"] });
			void client.invalidateQueries({
				queryKey: ["contacts"],
				predicate: (query) => query.queryKey[2] === "cards",
			});
		}
	}, delay);
}

/** Um quadro do stream: o som quando é fila, a chave certa quando é acontecimento. */
function handleFrame(client: QueryClient, frame: string): void {
	const line = frame.split("\n").find((l) => l.startsWith("data:"));
	if (!line) return;
	let event: { kind?: string; topic?: string };
	try {
		event = JSON.parse(line.slice(5).trim()) as { kind?: string; topic?: string };
	} catch {
		return; // Um quadro malformado custa aquele quadro.
	}
	if (event.kind === "queued" && enabled.value) {
		// O som segue sendo a escolha do operador; a lista, não.
		beep();
	}
	if (event.kind === "pulse" && event.topic === "deploy") {
		// O PAINEL FOI PUBLICADO (29/09): o aviso só ACORDA a checagem — quem
		// decide é o `version.json`, a promessa de que o `index.html` novo já
		// está no ar. O `version` do evento não é lido de propósito.
		void checkForNewDeploy();
		return;
	}
	if (event.kind === "queued" || event.kind === "inbound" || event.kind === "done") {
		refreshSoon(client, "inbox");
	} else if (event.kind === "pulse" && event.topic && event.topic in KEYS_OF) {
		// O crachá, o sino e o chip de créditos, na hora do acontecimento.
		refreshSoon(client, event.topic);
	} else if (event.kind === "pulse" && event.topic?.startsWith("records:")) {
		// A TABELA ABERTA (27/08): um registro escrito pelo agente, por uma
		// automação ou por um colega aparece na lista de quem está olhando. O
		// tópico é parametrizado — `records:<entidade>` — então não cabe no mapa
		// estático acima; a chave invalidada carrega a entidade, e o prefixo
		// `['records', code]` alcança lista, contadores e o registro aberto.
		// A mesma janela de 600ms dos irmãos: uma rajada de escrita (import,
		// automação em série) vira UMA rodada de refetch.
		const code = event.topic.slice("records:".length);
		if (code) refreshRecordsSoon(client, code);
	}
}

async function open(client: QueryClient, signal: AbortSignal): Promise<void> {
	try {
		// AWAITED: `getAuthToken` is a promise, and interpolating it raw sends
		// `Bearer [object Promise]` — a 401 on every open, and no alert at all.
		const token = await getAuthToken();
		if (!token || signal.aborted) return;

		// SEM barra do topo, de propósito — é a única abertura de stream que não a
		// acende. Esta ligação é da MOLDURA, vive a sessão inteira e se reabre
		// sozinha a cada queda: ninguém pediu por ela, e acender a barra a cada
		// reconexão (ou a cada 401 de uma aba que dormiu) seria ruído que ensina a
		// ignorar a barra. O stream de notificações fica de fora pela mesma razão.
		const response = await fetch("/v3/conversations/queue/stream", {
			headers: { Authorization: `Bearer ${token}` },
			signal,
		});
		if (!response.ok || !response.body) return;

		// A leitura que fecha o buraco: tudo o que aconteceu enquanto a conexão
		// esteve caída não mandou evento nenhum — reconciliar é no REGRESSO do
		// stream, não num relógio. Todas as chaves, porque o buraco não escolhe.
		for (const topic of Object.keys(KEYS_OF)) refreshSoon(client, topic);
		void client.invalidateQueries({ queryKey: ["records"] });
		void client.invalidateQueries({ queryKey: ["board"] });
		// E o deploy pelo mesmo buraco: o back reinicia e derruba todos os streams,
		// e um painel publicado enquanto ele estava fora nunca teve aviso. Perguntar
		// no regresso fecha essa janela sem relógio (29/09, ver `deploy-watch.ts`).
		void checkForNewDeploy();

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";
		while (!signal.aborted) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });
			// Quadros SSE terminam em linha em branco, e um quadro pode chegar partido
			// entre duas leituras — parsear por pedaço é como meio JSON chega ao parse.
			const frames = buffer.split("\n\n");
			buffer = frames.pop() ?? "";
			for (const frame of frames) handleFrame(client, frame);
		}
	} catch {
		// Uma conexão caída não quebra nada por muito tempo: o retry abaixo volta em
		// segundos e o regresso reconcilia. Mas este stream agora é o que atualiza a
		// caixa — não há mais relógio por trás dele.
	} finally {
		// Antes o stream renascia ao remontar o painel; quem o segura a sessão inteira
		// precisa voltar sozinho, senão a primeira queda o cala até o F5.
		if (!signal.aborted) setTimeout(() => void open(client, signal), RETRY_MS);
	}
}

/** Ausente é DESLIGADO. Ver a nota acima: ligar é uma decisão de quem ouve. */
function readStoredFlag(): boolean {
	try {
		return localStorage.getItem(SOUND_KEY) === "1";
	} catch {
		return false;
	}
}
